import { Swiper } from './swiper-DSEIACnL.js';
import { A as A11y } from './a11y-CNmE74mL.js';
import { M as Manipulation } from './manipulation-Bw96zpwn.js';
import './utils-CPPFXbqB.js';

/**
 * @typedef {import('swiper').Swiper} Swiper
 */


class ShopByRoom extends HTMLElement {
  #productsSwiperEl = this.querySelector('.swiper-products');
  #tabsSwiperEl = this.querySelector('.swiper-tabs');
  /** @type {Swiper} */
  #productsSwiperInstance = null;
  /** @type {Swiper} */
  #tabsSwiperInstance = null;
  #templates = [...this.querySelectorAll('template')];
  #tabs = [];
  #slots = {
    image: this.querySelector('[data-slot="image"]'),
    content: this.querySelector('[data-slot="content"]'),
    products: this.querySelectorAll('[data-slot="product"]'),
  };

  get #hotspots() {
    return this.querySelectorAll('.button--hotspot');
  }

  connectedCallback() {
    this.#initTabsSwiper();
    this.#initSwiperProducts();
    this.#tabs.forEach((tab) => {
      tab.addEventListener('click', () => this.#handleTabClick(tab));
    });
    this.#slots.products.forEach((product) => this.#createHotspot(product));
    this.#handleShopifyEvents();
  }

  #initTabsSwiper() {
    this.#tabsSwiperInstance = new Swiper(this.#tabsSwiperEl, {
      modules: [A11y, Manipulation],
      slidesPerView: 'auto',
      spaceBetween: 8,
    });

    this.#tabsSwiperInstance.removeAllSlides();

    this.#templates.forEach((template) => {
      const content = document.importNode(template.content, true);

      // Append tab slide
      const slide = content.querySelector('[data-slot="tab"]');
      this.#tabsSwiperInstance.appendSlide(slide);

      // Get tab button
      const tab = slide.querySelector('button[data-controls]');
      this.#tabs.push(tab);
    });
  }

  #initSwiperProducts() {
    this.#productsSwiperInstance = new Swiper(this.#productsSwiperEl, {
      modules: [A11y, Manipulation],
      slidesPerView: 1.3,
      spaceBetween: 16,
      breakpoints: {
        640: {
          slidesPerView: 2.3,
        },
        768: {
          slidesPerView: 1.3,
        },
        1024: {
          slidesPerView: 2.3,
        },
      },
    });
  }

  #handleTabClick(tab) {
    const template = this.#templates.find(({ id }) => id === tab.dataset.controls);
    const content = document.importNode(template.content, true);

    this.#tabs.forEach((tab) => tab.classList.replace('button--black', 'button--support'));
    tab.classList.replace('button--support', 'button--black');

    this.#replaceImage(content);
    this.#replaceProducts(content);
  }

  #replaceImage(content) {
    const image = content.querySelector('[data-slot="image"]');
    this.#slots.image.innerHTML = image.innerHTML;
  }

  #replaceProducts(content) {
    const slides = content.querySelectorAll('[data-slot="product"]');
    this.#productsSwiperInstance.removeAllSlides();
    this.#productsSwiperInstance.appendSlide(slides);
    slides.forEach((slide) => this.#createHotspot(slide));
  }

  #createHotspot(slide) {
    const hotspotTop = slide.dataset.hotspotTop;
    const hotspotLeft = slide.dataset.hotspotLeft;
    const hotspot = document.createElement('button');
    hotspot.type = 'button';
    hotspot.classList.add('button', 'button--hotspot');
    hotspot.style.top = `${hotspotTop}%`;
    hotspot.style.left = `${hotspotLeft}%`;
    hotspot.setAttribute('data-controls', slide.id);

    if (Shopify.designMode) {
      hotspot.setAttribute('data-shopify-editor-block', slide.dataset.shopifyEditorBlock);
    }

    hotspot.addEventListener('click', () => this.#handleHotspotClick(hotspot, slide));

    this.#slots.image.appendChild(hotspot);
  }

  #handleHotspotClick(hotspot, slide) {
    this.#hotspots.forEach((hotspot) => (hotspot.ariaCurrent = 'false'));
    hotspot.ariaCurrent = 'true';

    this.#productsSwiperInstance.slides.forEach((slide) => (slide.ariaCurrent = 'false'));
    slide.ariaCurrent = 'true';

    this.#slideToProduct(slide);
  }

  #handleShopifyEvents() {
    document.addEventListener('shopify:block:select', ({ target }) => {
      const block = document.getElementById(target.dataset.controls);

      // If the block is a product, slide to the product
      if (block.classList.contains('swiper-slide')) {
        this.#slideToProduct(block);
      }
      // If the block is a tab, handle the tab click
      else {
        this.#slideToProduct(target.closest('.swiper-slide'));
        this.#handleTabClick(target);
      }
    });
  }

  #slideToProduct(slide) {
    const slideIndex = this.#productsSwiperInstance.slides.indexOf(slide);
    this.#productsSwiperInstance.slideTo(slideIndex);
  }
}

customElements.define('shop-by-room', ShopByRoom);
